import React, { useEffect, useState } from "react";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/bnp-ui";
import { VerifiedBadge } from "@/components/bnp-icons";
import { PropertyInspectionModal } from "@/components/pages/user/dashboard/properties/Tabs/live-inspection/PropertyInspectionRequestModal";
import { safeMoment } from "@/utils/safeMoment";
import { configBootstrap } from "@/utils/env-bootstrap";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { useCurrency } from "@/hooks/useCurrency";
import { WhatsAppButton } from "./buttons/WhatsappButton";
import { PhoneCallButton } from "./buttons/PhoneCallButton";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";

interface PropDetailRealtorCardProps {
  property: any;
  realtor: any;
  onViewProperties?: () => void;
  hasRequested: boolean;
}

const PropDetailRealtorCard = ({
  property,
  realtor,
  onViewProperties,
  hasRequested,
}: PropDetailRealtorCardProps) => {
  const { user, status } = useAuth();
  const isAuthLoading = status === "idle" || status === "loading";
  const router = useRouter();
  const [liveInspectionOpen, setLiveInspectionOpen] = useState<boolean>(false);
  const RaldEmail = configBootstrap.raldEmail;

  const realtorIsRald = RaldEmail === realtor.email;

  const toggleLiveInspection = () => {
    setLiveInspectionOpen((prev) => !prev);
  };

  const { trackInspectionClicked } = usePostHogAnalytics();

  // Fired only on an explicit user click (the ?inspection=1 login-return
  // auto-open is intentionally not tracked as a click).
  const handleRequestInspectionClick = () => {
    if (property?.id) {
      trackInspectionClicked({
        property_id: property.id,
        listing_type: property?.listing_type,
        property_type: property?.property_type,
        location_city: property?.city,
        price: property?.price || property?.total_package,
        realtor_id: realtor?.id,
        developer_id:
          property?.createdBy?.role === "developer"
            ? property?.createdBy?.id
            : undefined,
      });
    }
    toggleLiveInspection();
  };

  useEffect(() => {
    if (router.query.inspection !== "1" || isAuthLoading) return;

    if (user) {
      setLiveInspectionOpen(true);
    }

    const [pathname, search] = router.asPath.split("?");
    const params = new URLSearchParams(search);
    params.delete("inspection");
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, undefined, {
      shallow: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.inspection, user, isAuthLoading]);

  const currency = property?.currency || "NGN";
  const propertyPrice = property?.price || property?.total_package;
  const totalPackage = property?.total_package;
  const is_negotiable = property?.is_negotiable;
  const phone = realtor?.phone || realtor?.phone_number;
  const propertyId = property?.id;
  const requestInspection = property?.request_inspection;
  const has_requested = hasRequested;
  const { formatPrice } = useCurrency();

  const hasPrice = propertyPrice !== null && propertyPrice !== undefined;
  const hasTotal = totalPackage !== null && totalPackage !== undefined;

  const realtorName =
    realtor?.business_name ||
    `${realtor?.first_name || ""} ${realtor?.last_name || ""}`.trim() ||
    "-";
  const realtorJoinedYears = safeMoment(realtor?.join_date)?.fromNow() ?? "-";

  const lead = {
    leadUserId: user?.id ?? null,
    isGuestUser: !user,
    realtorId: realtor?.id ?? null,
    propertyCreatedById: property?.createdBy?.id ?? null,
    leadUserRole: user?.role ?? null,
  };

  return (
    <div className="w-full bg-white border border-slate-200/80 rounded-2xl p-6 shadow-xs">
      {/* Price Section */}
      <div className="w-full flex flex-col items-center text-center pb-6 border-b border-slate-100">
        <div className="w-full flex items-center justify-between gap-2 mb-1 ">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
            Property Price
          </span>

          {is_negotiable && hasPrice && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200/60">
              Negotiable
            </span>
          )}
        </div>
        <div className="space-y-2 mb-1">
          <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            {hasPrice
              ? formatPrice(propertyPrice, currency)
              : "Price on request"}
          </h2>
        </div>

        <p className="text-xs sm:text-sm text-slate-500 font-normal">
          Total package:{" "}
          <span className="font-semibold text-slate-700">
            {hasTotal
              ? formatPrice(totalPackage, currency)
              : "Price on request"}
          </span>
        </p>

        {/* Primary Action Buttons */}
        <div className="w-full flex flex-col gap-2.5 mt-6">
          <WhatsAppButton
            phone={phone}
            propertyId={property.id}
            lead={lead}
            propertyContext={{
              property_type: property?.property_type,
              listing_type: property?.listing_type,
              location_city: property?.city,
              price: property?.price || property?.total_package,
              realtor_id: realtor?.id,
              developer_id:
                property?.createdBy?.role === "developer"
                  ? property?.createdBy?.id
                  : undefined,
            }}
          />

          {requestInspection && (
            <Button
              className="w-full h-11 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-800 text-sm font-semibold rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleRequestInspectionClick}
              disabled={has_requested}
            >
              {has_requested
                ? "Property Inspection Requested"
                : "Request Property Inspection"}
            </Button>
          )}
        </div>
      </div>

      {/* Realtor Details Metadata */}
      <div className="py-5 space-y-3 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-slate-500 font-normal">Realtor</span>
          <span className="font-semibold text-slate-900 max-w-[65%] text-right truncate flex items-center gap-1.5 justify-end">
            {realtor?.verified === "verified" && <VerifiedBadge />}
            <span className="truncate">{realtorName}</span>
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-slate-500 font-normal">Registered</span>
          <span className="font-medium text-slate-800">
            {realtorJoinedYears}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-slate-500 font-normal">Phone</span>
          <span className="font-medium text-slate-800">
            {phone ? phone : "-"}
          </span>
        </div>

        <PhoneCallButton
        phone={phone}
        propertyId={property.id}
        lead={lead}
        propertyContext={{
          property_type: property?.property_type,
          listing_type: property?.listing_type,
          location_city: property?.city,
          price: property?.price || property?.total_package,
          realtor_id: realtor?.id,
          developer_id:
            property?.createdBy?.role === "developer"
              ? property?.createdBy?.id
              : undefined,
        }}
      />
      </div>

      {/* Footer Navigation Link */}
      <button
        onClick={onViewProperties}
        className="w-full pt-4 border-t border-slate-100 text-xs font-semibold text-[#19545e] hover:text-[#13424a] hover:underline transition-colors text-center cursor-pointer"
      >
        View all properties from this agent
      </button>

      <PropertyInspectionModal
        isOpen={liveInspectionOpen}
        onClose={toggleLiveInspection}
        propertyTitle={""}
        propertyAddress={""}
        propertyId={propertyId}
        isRald={realtorIsRald}
      />
    </div>
  );
};

export default PropDetailRealtorCard;
